/* The hours in the days ahead that are actually yours.
 *
 * This replaced a points balance, and the replacement is the argument: the
 * rewards screen handed out a currency and let you spend it on a thing you had
 * defined yourself, which is a game laid over a calendar that was already
 * telling the truth underneath it. The truth is the more useful sentence —
 * *here are four free evenings this week; what goes in one?*
 *
 * Only off-hours windows are offered. Free time inside working hours is not
 * free, it is unallocated work time, and offering somebody their Tuesday
 * morning for the guitar is how a planner loses an argument with a job.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Hobby } from '@shared/types';
import { get, post, errorMessage } from '../../api';
import { useToast } from '../../ui/toast';
import { hhmm, human, nearLabel } from '../../ui/time';
import Section from '../../ui/Section';
import Icon from '../../ui/Icon';
import type { IconName } from '../../ui/Icon';

const WHEN_ICON: Record<string, IconName> = { morning: 'sun', afternoon: 'sun', evening: 'moon' };

interface FreeWindow {
  date: string;
  startMin: number;
  endMin: number;
  minutes: number;
  weekday: string;
  when: 'morning' | 'afternoon' | 'evening';
}

export default function FreeTime({ today, hobbies }: { today: string; hobbies: Hobby[] }) {
  const qc = useQueryClient();
  const toast = useToast();
  /* Which window is being filled, and with what. One at a time: a screen that
     lets you assign four evenings at once is a screen where you assign none. */
  const [filling, setFilling] = useState<FreeWindow | null>(null);
  const [what, setWhat] = useState('');

  const free = useQuery({
    queryKey: ['free', today],
    queryFn: async () => {
      const r = await get<{ windows: FreeWindow[] }>(`/api/free?from=${today}&days=7`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not work out your free time.'));
      return r.windows;
    }
  });

  const claim = useMutation({
    mutationFn: async ({ w, hobby, title }: { w: FreeWindow; hobby: Hobby | null; title: string }) => {
      const r = await post('/api/plan/block', {
        date: w.date,
        startMin: w.startMin,
        /* The window, or an hour of it — whichever is smaller. Filling a
           four-hour evening entirely with "read" is not what anybody means by
           designating time. */
        minutes: Math.min(w.minutes, hobby ? Math.max(45, Math.min(120, w.minutes)) : 60),
        title,
        kind: 'ritual',
        typeId: hobby?.typeId ?? null
      });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not put it there.'));
      return title;
    },
    onSuccess: title => {
      void qc.invalidateQueries({ queryKey: ['free'] });
      void qc.invalidateQueries({ queryKey: ['plan'] });
      void qc.invalidateQueries({ queryKey: ['week'] });
      void qc.invalidateQueries({ queryKey: ['hobbies'] });
      setFilling(null);
      setWhat('');
      toast.say(`${title} is on your calendar. It is yours — the planner works around it.`, 'good');
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const windows = free.data ?? [];
  /* Evenings and weekends first: they are the hours people actually mean when
     they say they have no time. */
  const shown = windows.slice(0, 8);

  return (
    <Section id="free-title" icon="clock" className="tone-green"
             title="Time that is yours"
             meta={windows.length
               ? `${human(windows.reduce((a, w) => a + w.minutes, 0))} free in the next seven days`
               : 'the next seven days, outside working hours'}
             info={<>Not a reward and nothing to spend: these are simply the hours nothing is
               planned in, outside your working hours. Put something in one, or do not — but you
               cannot say there was no time without disagreeing with the list.</>}>

      {free.isError && <p className="auth-err" role="alert">{(free.error as Error).message}</p>}

      {!free.isPending && windows.length === 0 && (
        <p className="muted small">
          Nothing free in the next week outside your working hours — which is either a
          full week or a sleep window and working day that leave no room. Both are worth
          knowing; the second one is in Settings.
        </p>
      )}

      {shown.length > 0 && (
        <>
          <ul className="free-list">
            {shown.map(w => {
              const key = `${w.date}-${w.startMin}`;
              const open = filling && `${filling.date}-${filling.startMin}` === key;
              return (
                <li key={key} className={open ? 'is-open' : ''}>
                  <div className="free-when">
                    <span className="item-icon" aria-hidden="true"><Icon name={WHEN_ICON[w.when] ?? 'clock'} size={17} /></span>
                    <div className="item-body">
                      <strong>
                        {nearLabel(w.date, today) ?? w.weekday}
                        {' '}{w.when}
                      </strong>
                      <span className="muted small">
                        {hhmm(w.startMin)}–{hhmm(w.endMin)} · {human(w.minutes)} free
                      </span>
                    </div>
                  </div>

                  {!open && (
                    <button className="btn btn-ghost btn-sm"
                            onClick={() => { setFilling(w); setWhat(''); }}>
                      <Icon name="plus" size={16} />Use it
                    </button>
                  )}

                  {open && (
                    <div className="free-fill">
                      {hobbies.length > 0 && (
                        <div className="inline wrap">
                          {hobbies.map(h => (
                            <button key={h.typeId} className="btn btn-ghost btn-sm"
                                    disabled={claim.isPending}
                                    onClick={() => claim.mutate({ w, hobby: h, title: h.name })}>
                              <Icon name="hobbies" size={14} />{h.name}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="inline">
                        <input value={what} maxLength={120}
                               aria-label={`What goes in ${w.weekday} ${w.when}`}
                               placeholder="Or something else — see Sam, read"
                               onChange={e => setWhat(e.target.value)} />
                        <button className="btn btn-primary btn-sm"
                                disabled={!what.trim() || claim.isPending}
                                onClick={() => claim.mutate({ w, hobby: null, title: what.trim() })}>
                          Put it here
                        </button>
                        <button className="btn btn-ghost btn-sm"
                                onClick={() => setFilling(null)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {windows.length > shown.length && (
            <p className="muted small">
              And {windows.length - shown.length} more — the week has more room in it than
              it feels like it does.
            </p>
          )}
        </>
      )}
    </Section>
  );
}
